import axios from 'axios';
import {LOCAL_STORAGE_KEYS} from "../utils/constants";
import {toastWarning} from "../utils/utils";
import {refreshToken} from "../services/auth.service";
import {ECC_cipher_helper} from "../utils/ecc_cipher_util";

axios.defaults.headers.post['Content-Type'] = 'application/json';
const base_uri = process.env.REACT_APP_MYDQ_API;

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach(promise => {
        if (error) {
            promise.reject(error);
        } else {
            promise.resolve(token);
        }
    });

    failedQueue = [];
};

function invokeApiWihToken(reqParams, token) {
    const {headers} = reqParams;
    const bearer = typeof token === 'string' ? token : '';
    const modifiedHeaders = {...headers, Authorization: `Bearer ${bearer}`};
    const modifiedParams = {...reqParams, headers: modifiedHeaders};
    return invokeApi(modifiedParams);
}

function extractSessionFromRefreshResponse(response) {
    if (response == null || response === false) return null;
    const d = response.data;
    if (d && typeof d.auth_token === 'string') {
        return {auth_token: d.auth_token, user: d.user};
    }
    if (d && d.data && typeof d.data.auth_token === 'string') {
        return {auth_token: d.data.auth_token, user: d.data.user};
    }
    return null;
}

function retryRequestWithRefresh(reqParams) {
    return new Promise((resolve, reject) => {
        refreshToken()
            .then((response) => {
                const session = extractSessionFromRefreshResponse(response);
                if (!session || !session.auth_token) {
                    processQueue(new Error('Session refresh failed'), null);
                    reject(new Error('Session refresh failed'));
                    return;
                }
                const {auth_token, user} = session;
                localStorage.setItem(LOCAL_STORAGE_KEYS.auth_token, JSON.stringify(auth_token));
                localStorage.setItem(LOCAL_STORAGE_KEYS.auth_user, JSON.stringify(user));
                processQueue(null, auth_token);
                resolve(invokeApiWihToken(reqParams, auth_token));
            })
            .catch((err) => {
                processQueue(err, null);
                reject(err);
            })
            .finally(() => {
                isRefreshing = false;
            });
    });
}

function putRequestInQueue(reqParams) {
    return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject })
    })
    .then(token => invokeApiWihToken(reqParams, token)) // Retry request with new token
    .catch(err => Promise.reject(err));
}

function forceLogout() {
    localStorage.clear();
    window.location.reload();
}

function getRefreshToken() {
    return JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEYS.refresh_token));
}

/**
 * Is this failure genuinely a connectivity problem?
 *
 * ORDER MATTERS. A response — any response, including 419 or 500 — proves the
 * connection works, so it is checked FIRST. Consulting navigator.onLine first
 * (as this did) turned every expired-token 419 into "No internet connection"
 * and stopped the refresh branch from ever running: PBDQ-154.
 *
 * navigator.onLine only reports whether the OS has a network interface, not
 * whether anything is reachable, and it flips spuriously on VPN changes, Wi-Fi
 * handoff and sleep/wake. It is now the LAST resort, used only when there is no
 * response to contradict it.
 */
function isNetworkFailure(error) {
    // The server answered — definitively not a connectivity problem.
    if (error && error.response) return false;

    const msg = String((error && error.message) || '').toLowerCase();
    if (msg.includes('network') || (error && error.code === 'ERR_NETWORK')) return true;

    // No response and no network-shaped error: fall back to the browser's hint.
    return typeof navigator !== 'undefined' && !navigator.onLine;
}

export async function invokeApi(reqParams, use_e2ee=false) {

    const {
        path,
        method = 'GET',
        headers = {},
        queryParams = {},
        postData = {},
        ...rest
    } = reqParams;

    let reqObj = {
        method  : method,
        url     : base_uri + path,
        headers : headers,
        ...rest
    };
    reqObj['params'] = queryParams;

    if (method === 'POST') {
        let e2ee_payload = false;
        let e2ee_obj_str, e2ee_obj_keypair;
        if (use_e2ee) {
            const e2ee_obj_b64 = localStorage.getItem(LOCAL_STORAGE_KEYS.e2ee_krypto_pair_obj);
            if (e2ee_obj_b64) e2ee_obj_str = atob(e2ee_obj_b64);
            if (e2ee_obj_str) e2ee_obj_keypair = JSON.parse(e2ee_obj_str);
        }
        if (e2ee_obj_keypair && e2ee_obj_keypair.pub) {
            e2ee_payload = true;

            // encrypt using ECC-keys
            const publicKey = JSON.parse(atob(e2ee_obj_keypair.pub))
            const payloadString = JSON.stringify(postData);
            const payload = await ECC_cipher_helper.encryptData(payloadString, publicKey);

            reqObj['data'] = {
                payload,
                e2ee_payload
            }
        }
        else {
            reqObj['data'] = postData
        }
    }

    let results = undefined;

    try {
        results = await axios(reqObj);

        /**
         * A completed round trip is proof the connection works.
         *
         * The offline banner is driven by `navigator.onLine`, which the browser
         * sometimes reports incorrectly — that is how PBDQ-154 was captured with
         * "You're offline" on screen while the very same page was receiving HTTP
         * 419 responses from the API. Announcing a real success lets the banner
         * correct itself instead of sitting there contradicting the network tab.
         */
        if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('api-reachable'));
        }
        if (results.data.data && results.data.data.e2ee_data) {
            // need to decrypt the response
            let e2ee_obj_str, e2ee_obj_keypair;
            const e2ee_obj_b64 = localStorage.getItem(LOCAL_STORAGE_KEYS.e2ee_krypto_pair_obj);
            if (e2ee_obj_b64) e2ee_obj_str = atob(e2ee_obj_b64);
            if (e2ee_obj_str) e2ee_obj_keypair = JSON.parse(e2ee_obj_str);
            if (e2ee_obj_keypair && e2ee_obj_keypair.pvt) {
                const privateKey = JSON.parse(atob(e2ee_obj_keypair.pvt));
                const cipher_data = results.data.data.secure_data;
                const responseString = await ECC_cipher_helper.decryptData(cipher_data, privateKey);
                const responseData = JSON.parse(responseString);
                if (Array.isArray(responseData)) results.data.data = [...responseData];
                else results.data.data = {...responseData};
                if (!use_e2ee) console.log(`QA:MODE: path: ${path} --> E2EE data received:`, results.data.data);
            } else {
                toastWarning(`System security protocols are update! 
                Your current session is forced logged out!
                Please re-login to adopt the improved system.`);

                setTimeout(() => {
                    forceLogout();
                }, 4000);

                return false;
            }
        }
        return results.data;
    } catch (error) {
        console.log('invoke API :: Error: ', error);

        if (isNetworkFailure(error)) {
            throw new Error('NETWORK ERROR : No internet connection');
        }

        const status = error.response?.status;

        if (status === 499) throw new Error('Permission denied');

        if (status === 401) {
            forceLogout();
            throw new Error('Unauthorized');
        }

        if (status === 419) {
            if (!getRefreshToken()) {
                forceLogout();
                throw new Error('Unauthorized');
            }
            if (isRefreshing) return putRequestInQueue(reqParams);
            isRefreshing = true;
            return retryRequestWithRefresh(reqParams);
        }

        if (!(error.response && error.response.data && error.response.data.message)) {
            toastWarning('NETWORK ERROR : Some Thing Went Wrong!');
            throw new Error('NETWORK ERROR : Some Thing Went Wrong!');
        }
        toastWarning(error.response.data.message);
        throw new Error(error.response.data.message);
    }
}
